import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { hatchingAPI, farmAPI } from '../../services/api';
import { SparklesIcon, PlusIcon } from '@heroicons/react/24/outline';
import { toLocalISO } from '../../utils/dates';

const ACTIVE_STATUSES = new Set(['set', 'incubating', 'lockdown']);

const Hatching = () => {
  const { currentFarm } = useFarm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState('');

  const load = async () => {
    if (!currentFarm) return;
    try {
      setLoading(true);
      const res = await hatchingAPI.listBatches(currentFarm.id);
      setBatches(res.data?.batches || []);
    } catch (e) {
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [currentFarm]);

  const activeBatches = batches.filter((b) => ACTIVE_STATUSES.has(b.status)).length;
  const totalEggs = batches.reduce((sum, b) => sum + (b.eggsSet || 0), 0);

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <SparklesIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Hatching</h1>
              <p className="text-green-100">Plan and track incubation and hatch</p>
            </div>
          </div>
          <button
            className="inline-flex items-center px-5 py-2.5 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors font-medium disabled:opacity-50"
            onClick={() => {
              if (!currentFarm?.id) { alert('Please create/select a farm first.'); navigate('/farm/setup'); return; }
              setShowCreate(true);
            }}
            disabled={!currentFarm?.id}
          >
            <PlusIcon className="h-5 w-5 mr-2" /> Start Batch
          </button>
        </div>
        <div className="relative mt-6 grid grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{batches.length}</p>
            <p className="text-green-200 text-xs">Total Batches</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{activeBatches}</p>
            <p className="text-green-200 text-xs">In progress</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{totalEggs}</p>
            <p className="text-green-200 text-xs">Eggs Set</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-500">Loading...</div>
        ) : batches.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No batches yet.</div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-gray-100">
              {batches.map((b) => (
                <div key={b.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{b.incubationType}</p>
                      <p className="text-xs text-gray-500">Set {b.setDate} · Due {b.dueDate}</p>
                    </div>
                    <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{b.status}</span>
                  </div>
                  <p className="text-sm text-gray-600">{b.eggsSet} eggs set{b.chicksHatched != null ? ` · ${b.chicksHatched} hatched` : ''}</p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Link className="btn-outline text-xs" to={`/hatching/${b.id}`}>Open</Link>
                    <button className="btn-outline text-xs" onClick={() => setEditId(b.id)}>Edit</button>
                    <button className="btn-outline text-xs" onClick={async () => {
                      if (!window.confirm('Delete this batch? This action cannot be undone.')) return;
                      try { await hatchingAPI.deleteBatch(b.id); load(); } catch (e) { alert(e.response?.data?.message || 'Failed to delete'); }
                    }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Set Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Due</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Eggs Set</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {batches.map((b) => (
                    <tr key={b.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 capitalize">{b.incubationType}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.setDate}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.dueDate}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.eggsSet}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 capitalize">{b.status}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="inline-flex gap-2">
                          <Link className="btn-outline" to={`/hatching/${b.id}`}>Open</Link>
                          <button className="btn-outline" onClick={() => setEditId(b.id)}>Edit</button>
                          <button className="btn-outline" onClick={async () => {
                            if (!window.confirm('Delete this batch? This action cannot be undone.')) return;
                            try { await hatchingAPI.deleteBatch(b.id); load(); } catch (e) { alert(e.response?.data?.message || 'Failed to delete'); }
                          }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showCreate && (
        <CreateBatchModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} farmId={currentFarm?.id} creating={creating} setCreating={setCreating} />
      )}
      {editId && (
        <EditBatchModal id={editId} onClose={() => setEditId('')} onUpdated={() => { setEditId(''); load(); }} />
      )}
    </div>
  );
};

const CreateBatchModal = ({ onClose, onCreated, farmId, creating, setCreating }) => {
  const [cohorts, setCohorts] = useState([]);
  const [useCustomBreed, setUseCustomBreed] = useState(false);
  useEffect(() => { (async () => { try { const res = await farmAPI.listCohorts(farmId); setCohorts(res.data?.cohorts || []); } catch {} })(); }, [farmId]);
  const breedOptions = ['Kenbro', 'Kuroiler', 'Sasso', 'Rainbow Rooster', 'Cobb 500', 'Ross 308', 'Hubbard', 'Hy-Line Brown', 'ISA Brown', 'Lohmann Brown', 'Indigenous (Kienyeji)'];
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!farmId) { alert('No farm selected. Please create/select a farm first.'); onClose(); return; }
    const form = new FormData(e.currentTarget);
    const payload = {
      incubationType: form.get('incubationType'),
      setDate: form.get('setDate'),
      eggsSet: Number(form.get('eggsSet') || 0),
      breed: (useCustomBreed ? (form.get('breedCustom') || '') : (form.get('breed') || '')) || undefined,
      sourceCohortId: form.get('sourceCohortId') || undefined,
      incubatorName: form.get('incubatorName') || undefined,
      notes: form.get('notes') || undefined
    };
    try {
      setCreating(true);
      await hatchingAPI.createBatch(farmId, payload);
      onCreated();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to start batch');
    } finally { setCreating(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold">Start Incubation Batch</h3>
          <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="label">Type *</label>
            <select name="incubationType" required className="input">
              <option value="incubator">Incubator</option>
              <option value="broody">Broody</option>
            </select>
          </div>
          <div>
            <label className="label">Set Date *</label>
            <input type="date" name="setDate" required className="input" defaultValue={toLocalISO(new Date())} />
          </div>
          <div>
            <label className="label">Eggs Set *</label>
            <input type="number" name="eggsSet" min="1" required className="input" />
          </div>
          <div>
            <label className="label">Breed</label>
            <select name="breed" className="input" onChange={(e) => setUseCustomBreed(e.target.value === 'custom')} defaultValue="">
              <option value="">Select (optional)</option>
              {breedOptions.map((b) => <option key={b} value={b}>{b}</option>)}
              <option value="custom">Other…</option>
            </select>
            {useCustomBreed && (
              <input name="breedCustom" className="input mt-2" placeholder="Enter custom breed" />
            )}
          </div>
          <div>
            <label className="label">Source flock</label>
            <select name="sourceCohortId" className="input">
              <option value="">None / Purchased eggs</option>
              {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Incubator Name</label>
            <input name="incubatorName" className="input" placeholder="Optional" />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea name="notes" rows={3} className="input" placeholder="Optional"></textarea>
          </div>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Starting...' : 'Start Batch'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBatchModal = ({ id, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  React.useEffect(() => { (async () => { try { const res = await hatchingAPI.getBatch(id); setData(res.data?.batch || null); } finally { setLoading(false); } })(); }, [id]);
  if (loading) return null;
  if (!data) return null;
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const payload = {
      incubationType: form.get('incubationType') || undefined,
      setDate: form.get('setDate') || undefined,
      dueDate: form.get('dueDate') || undefined,
      eggsSet: form.get('eggsSet') ? Number(form.get('eggsSet')) : undefined,
      breed: form.get('breed') || undefined,
      incubatorName: form.get('incubatorName') || undefined,
      notes: form.get('notes') || undefined
    };
    try { setSaving(true); await hatchingAPI.updateBatch(id, payload); onUpdated(); } catch (e) { alert(e.response?.data?.message || 'Failed to update'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
          <h3 className="font-semibold">Edit Incubation Batch</h3>
          <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={onSubmit} className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="label">Type</label>
            <select name="incubationType" className="input" defaultValue={data.incubationType}>
              <option value="incubator">Incubator</option>
              <option value="broody">Broody</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Set Date</label>
              <input type="date" name="setDate" className="input" defaultValue={data.setDate} />
            </div>
            <div>
              <label className="label">Due Date</label>
              <input type="date" name="dueDate" className="input" defaultValue={data.dueDate} />
            </div>
          </div>
          <div>
            <label className="label">Eggs Set</label>
            <input type="number" name="eggsSet" min="1" className="input" defaultValue={data.eggsSet} />
          </div>
          <div>
            <label className="label">Breed</label>
            <input name="breed" className="input" defaultValue={data.breed || ''} />
          </div>
          <div>
            <label className="label">Incubator Name</label>
            <input name="incubatorName" className="input" defaultValue={data.incubatorName || ''} />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea name="notes" rows={3} className="input" defaultValue={data.notes || ''} />
          </div>
          <div className="pt-2 flex items-center justify-end gap-2">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Hatching;
